<template>
  <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6">
      <h2 class="text-lg font-bold mb-4">Создать заказ на производство</h2>
      <form @submit.prevent="createOrder">
        <!-- Чекбокс для создания без коммерческого заказа (скрыт если передан preselectedOrder) -->
        <div v-if="!preselectedOrder" class="mb-4">
          <label class="flex items-center">
            <input
              type="checkbox"
              v-model="createWithoutClientOrder"
              class="mr-2"
              @change="onCreateModeChange"
            />
            <span class="text-sm font-medium text-gray-700">Создать без коммерческого заказа</span>
          </label>
        </div>

        <!-- Выбор коммерческого заказа (показывается только если не выбран чекбокс) -->
        <div v-if="!createWithoutClientOrder" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Коммерческий заказ</label>
          <v-select
            v-model="newOrder.client_order_id"
            :reduce="order => order"
            :options="clientOrders"
            :filterable="false"
            :disabled="!!preselectedOrder"
            label="number"
            placeholder="Начните вводить номер заказа..."
            :loading="loadingOrders"
            @search="onOrderSearch"
            @update:modelValue="newOrder.tech_map_id = null"
            required
          >
            <template #search="{attributes, events}">
              <input
                class="vs__search"
                :required="!newOrder.client_order_id"
                v-bind="attributes"
                v-on="events"
              />
            </template>
            <template #no-options="{ search, searching, loading }">
              <span v-if="loadingOrders">Загружается...</span>
              <span v-else>Заказы не найдены</span>
            </template>
          </v-select>
        </div>

        <!-- Выбор изделия -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Изделие</label>
          <v-select
            v-model="newOrder.product_id"
            :reduce="product => product.id"
            :options="availableProducts"
            label="name"
            placeholder="Выберите изделие"
            @update:modelValue="onProductChange"
            required
          >
            <template #option="option">
              <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
            </template>
            <template #selected-option="option">
              <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
            </template>
            <template #search="{attributes, events}">
              <input
                class="vs__search"
                :required="!newOrder.product_id"
                v-bind="attributes"
                v-on="events"
              />
            </template>
            <template #no-options="{ search, searching, loading }">
              Изделия не найдены.
            </template>
          </v-select>
        </div>

        <!-- Остальные поля формы остаются без изменений -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Количество</label>
          <input
            type="number"
            v-model="newOrder.quantity"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите количество"
          />
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Дата и время старта</label>
          <input
            type="datetime-local"
            v-model="newOrder.start_datetime"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Дата и время завершения</label>
          <input
            type="datetime-local"
            v-model="newOrder.end_datetime"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Спецификация</label>
          <v-select
            v-model="newOrder.tech_map_id"
            :reduce="techMap => techMap.id"
            :options="filteredTechMaps"
            label="name"
            required
            placeholder="Выберите спецификацию"
          >
            <template #search="{attributes, events}">
              <input
                class="vs__search"
                :required="!newOrder.tech_map_id"
                v-bind="attributes"
                v-on="events"
              />
            </template>
            <template #no-options="{ search, searching, loading }">
              <span v-if="!newOrder.product_id">Сначала выберите изделие</span>
              <span v-else>Спецификации не найдены</span>
            </template>
          </v-select>
        </div>
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            type="submit"
            :disabled="creating"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {{ creating ? 'Создание...' : 'Создать' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from "moment";

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    preselectedOrder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      newOrder: {
        client_order_id: null,
        product_id: null,
        quantity: null,
        start_datetime: moment().format('YYYY-MM-DDTHH:mm'),
        end_datetime: moment().add(3,'hours').format('YYYY-MM-DDTHH:mm'),
        tech_map_id: null,
      },
      clientOrders: [], // Список коммерческих заказов
      techMaps: [], // Список спецификаций
      loading: true, // Флаг загрузки данных
      loadingOrders: false,
      orderSearchTimer: null,
      createWithoutClientOrder: false, // Флаг для создания заказа без коммерческого заказа
      creating: false, // Флаг отправки — блокирует повторное создание (защита от дублей)
      idempotencyToken: null, // Токен идемпотентности одной попытки создания
    };
  },
  computed: {
    /**
     * Возвращает продукцию, доступную для выбранного коммерческого заказа,
     * или все продукты со спецификациями, если создается заказ без коммерческого заказа.
     */
    availableProducts() {
      if (this.createWithoutClientOrder) {
        // Если создаем без коммерческого заказа, возвращаем все продукты из спецификаций
        return this.techMaps.map(techMap => techMap.product).filter((product, index, self) => 
          index === self.findIndex(p => p.id === product.id)
        );
      } else if (this.newOrder.client_order_id) {
        // Если заказ выбран, берем продукцию из заказа
        return this.newOrder.client_order_id?.products || [];
      }
      return [];
    },
    /**
     * Фильтрует список спецификаций по выбранному изделию.
     */
    filteredTechMaps() {
      if (!this.newOrder.product_id) {
        return []; // Если изделие не выбрано, возвращаем пустой список
      }
      
      if (this.createWithoutClientOrder) {
        // Если создаем без коммерческого заказа, фильтруем по product_id напрямую
        return this.techMaps.filter((techMap) => techMap.product_id === this.newOrder.product_id);
      } else {
        // Если создаем с коммерческим заказом, используем старую логику
        const clientOrder = this.clientOrders.find(order => order.products.find(product => product.id === this.newOrder.product_id));
        if (!clientOrder) {
          return [];
        }

        const product = clientOrder.products.find(product => product.id === this.newOrder.product_id);
        return this.techMaps.filter((techMap) => techMap.product_id === product.product_id);
      }
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.clientOrders = [];
        this.techMaps = [];
        if (this.preselectedOrder) {
          this.createWithoutClientOrder = false;
          this.clientOrders = [this.preselectedOrder];
          this.newOrder.client_order_id = this.preselectedOrder;
        } else {
          this.onOrderSearch('');
        }
        this.fetchTechMaps();
      }
    },
    'newOrder.client_order_id'() {
      // При смене заказа сбрасываем выбранное изделие
      this.newOrder.product_id = null;
      this.newOrder.quantity = null;
      // Автовыбор: если одна продукция или одна непокрытая
      this.$nextTick(() => {
        if (this.availableProducts.length === 1) {
          this.newOrder.product_id = this.availableProducts[0].id;
          this.onProductChange(this.availableProducts[0].id);
        } else if (this.preselectedOrder?.production_counts && this.availableProducts.length > 1) {
          const uncovered = this.availableProducts.filter(p => {
            const produced = Number(this.preselectedOrder.production_counts[p.id] || 0);
            return produced < Number(p.count);
          });
          if (uncovered.length === 1) {
            this.newOrder.product_id = uncovered[0].id;
            this.onProductChange(uncovered[0].id);
          }
        }
      });
    },
  },
  methods: {
    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
    onOrderSearch(search) {
      clearTimeout(this.orderSearchTimer);
      this.orderSearchTimer = setTimeout(async () => {
        this.loadingOrders = true;
        try {
          const response = await axios.post('/api/order/getOrders', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          });
          this.clientOrders = response.data.data?.data || [];
        } catch (error) {
          console.error('Ошибка при загрузке заказов:', error);
          this.clientOrders = [];
        } finally {
          this.loadingOrders = false;
        }
      }, 350);
    },
    async fetchTechMaps() {
      try {
        const response = await axios.post('/api/techMap/getTechMaps', {
          page: 1,
          per_page: 500
        });
        this.techMaps = response.data.data?.data || [];
      } catch (error) {
        console.error('Ошибка при загрузке спецификаций:', error);
        this.techMaps = [];
      }
    },
    // Генерация токена идемпотентности (uuid) с fallback
    genToken() {
      if (window.crypto && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
    async createOrder() {
      // Защита от повторного нажатия на медленной сети (иначе плодятся дубли заказов)
      if (this.creating) return;

      // Проверяем, что поле "Коммерческий заказ" заполнено, если не выбран режим создания без него
      if (!this.createWithoutClientOrder && !this.newOrder.client_order_id) {
        alert('Поле "Коммерческий заказ" обязательно для заполнения');
        return;
      }

      // Один токен на попытку: ретраи используют тот же токен (бэк не создаст дубль),
      // после успешного создания генерируется новый.
      if (!this.idempotencyToken) {
        this.idempotencyToken = this.genToken();
      }

      this.creating = true;
      try {
        const response = await axios.post('/api/productionOrder/createOrEditProductionOrder', {
          ...this.newOrder,
          client_order_id: this.newOrder.client_order_id?.id || null,
          idempotency_token: this.idempotencyToken,
        });

        const createdId = response.data?.data?.id || null;

        // Уведомляем родителя о создании заказа
        this.$emit('order-created', createdId);

        this.idempotencyToken = this.genToken();

        // Закрываем модальное окно
        this.closeModal();

        // Очищаем форму
        this.resetForm();
      } catch (error) {
        console.error('Ошибка при создании заказа:', error);
        alert(error?.response?.data?.message || 'Не удалось создать заказ, попробуйте ещё раз');
      } finally {
        this.creating = false;
      }
    },
    resetForm() {
      this.newOrder = {
        client_order_id: null,
        product_id: null,
        quantity: null,
        start_datetime: moment().format('YYYY-MM-DDTHH:mm'),
        end_datetime: moment().add(3,'hours').format('YYYY-MM-DDTHH:mm'),
        tech_map_id: null,
      };
      this.createWithoutClientOrder = false;
    },
    onProductChange(productId) {
      this.newOrder.tech_map_id = null;
      // Подставляем количество из заказа покупателя (за вычетом уже в производстве)
      if (!this.createWithoutClientOrder && this.newOrder.client_order_id && productId) {
        const orderProduct = this.newOrder.client_order_id.products?.find(p => p.id === productId);
        if (orderProduct) {
          const produced = Number(this.preselectedOrder?.production_counts?.[productId] || 0);
          const remaining = Math.max(0, Number(orderProduct.count) - produced);
          this.newOrder.quantity = remaining > 0 ? remaining : orderProduct.count;
        }
      }
    },
    onCreateModeChange() {
      // При изменении режима создания сбрасываем выбранные значения
      this.newOrder.client_order_id = null;
      this.newOrder.product_id = null;
      this.newOrder.tech_map_id = null;
    },
  },
};
</script>

<style>
.v-select::placeholder, .vs__search::placeholder, .vs__dropdown-toggle {
  color: oklch(0.707 0.022 261.325);
}

/* Стили для переноса текста в селектах */
.vs__dropdown-option {
  white-space: normal !important;
  word-wrap: break-word !important;
  padding: 8px 12px !important;
  line-height: 1.4 !important;
  min-height: auto !important;
}

.vs__selected {
  white-space: normal !important;
  word-wrap: break-word !important;
  line-height: 1.4 !important;
  padding: 4px 0 !important;
}

.vs__dropdown-toggle {
  min-height: 42px !important;
  padding: 4px 0 !important;
}

/* Для корректного отображения длинного текста в поле ввода */
.vs__search {
  white-space: normal !important;
  word-wrap: break-word !important;
}
</style>
